import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/locale';
import en from '@/locales/en.json';
import { CATALOG_VERSION } from '@/locales/catalogVersion';

/**
 * i18next setup.
 *
 * `src/locales/en.json` is the source of truth for interface copy. It is hand-maintained, imported
 * here as the English resource bundle, and bundled with the app, so English never depends on a
 * network call and cannot fail to load.
 *
 * Call sites are bare keys:
 *
 *     t('landing.inspiration.findYours')
 *
 * Spanish is fetched from `GET /api/translations?locale=es`, which builds it from the same
 * catalogue via Azure Translator and caches it. No Spanish is committed to this repository, so a
 * correction will not need a deploy once the override layer exists.
 *
 * `npm run i18n:check` verifies that every key used in source exists in en.json, and reports keys in
 * en.json that nothing uses. Run it before committing; a missing key renders the key name on screen.
 *
 * See docs/plans/spanish-translation/static-ui-strings.md.
 */
void i18next
    .use(initReactI18next)
    .init({
        lng: DEFAULT_LOCALE,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: [...SUPPORTED_LOCALES],
        resources: {
            en: { translation: en },
        },
        interpolation: {
            // React escapes for us; double-escaping mangles anything containing an ampersand.
            escapeValue: false,
        },
        // An empty string in a catalogue should fall back to English, not render blank.
        returnEmptyString: false,
    });

const loaded = new Set<SupportedLocale>([DEFAULT_LOCALE]);

/**
 * Fetch and install the catalogue for a non-English locale.
 *
 * Safe to call repeatedly: a locale is fetched once per page load. Any failure is logged and
 * swallowed, leaving English in place, because a translation outage should never be a broken
 * interface.
 */
export async function loadCatalog(locale: SupportedLocale): Promise<void> {
    if (loaded.has(locale)) {
        return;
    }
    loaded.add(locale);

    try {
        // Versioned path: the URL is the hash of the catalogue this build renders from, so the
        // response is immutable and cacheable at the edge, and we can never be served strings from
        // a different build.
        const response = await fetch(
            `/api/translations/${encodeURIComponent(locale)}/${CATALOG_VERSION}`,
            { headers: { Accept: 'application/json' } },
        );
        if (!response.ok) {
            console.error('[i18n] catalogue request failed', response.status);
            return;
        }

        const { strings, status } = await response.json() as {
            strings: Record<string, unknown>;
            status: string;
        };
        if (status !== 'ok') {
            // The API could not build this catalogue version. English stays, which is correct.
            console.error('[i18n] catalogue unavailable', status);
            loaded.delete(locale);
            return;
        }
        i18next.addResourceBundle(locale, 'translation', strings, true, true);
        // The bundle arrives after init, so consumers need a nudge to re-render with it.
        void i18next.changeLanguage(i18next.language);
    }
    catch (err) {
        console.error('[i18n] catalogue request threw', err);
        loaded.delete(locale);
    }
}

export { i18next };
